import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import { PageHeader, GlassCard, GradButton, EmptyState, ConfirmDelete, TIP_ETIKET } from "@/components/erp";
import { FormDialog } from "@/components/FormDialog";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";

const TIPLER = ["uretim", "montaj", "kalite", "paketleme", "sevkiyat"];

export default function Istasyonlar() {
  const { isYonetim } = useAuth();
  const [list, setList] = useState([]);
  const [loks, setLoks] = useState([]);
  const [dlg, setDlg] = useState(null);

  const load = useCallback(async () => {
    const [s, l] = await Promise.all([api.get("/istasyonlar"), api.get("/lokasyonlar")]);
    setList(s.data); setLoks(l.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const fields = [
    { k: "ad", label: "İstasyon adı *", wide: true }, { k: "kod", label: "Kod *" },
    { k: "tip", label: "Tip", type: "select", options: TIPLER.map((t) => ({ value: t, label: TIP_ETIKET[t] })) },
    { k: "sira", label: "Sıra", type: "number" }, { k: "kapasite_gunluk", label: "Günlük kapasite", type: "number" },
    { k: "saatlik_maliyet", label: "Saatlik maliyet", type: "number" },
    { k: "maliyet_para", label: "Maliyet para birimi", type: "select", options: ["USD", "EUR", "TRY"].map((p) => ({ value: p, label: p })) },
    { k: "lokasyon_id", label: "Lokasyon", type: "select", options: loks.map((l) => ({ value: l.id, label: l.ad })) },
    { k: "aktif", label: "Aktif", type: "switch" },
    { k: "gunluk_calisma_saat", label: "Günlük çalışma saati (doluluk raporu)", type: "number" },
    { k: "fire_nedenleri", label: "Fire nedenleri (her satıra bir neden)", type: "liste", wide: true, placeholder: "Örn. Kenar düzeltme\nHamur ince düştü" },
  ];

  const kaydet = async (f0) => {
    const f = { ...f0, gunluk_calisma_saat: Number(f0.gunluk_calisma_saat) || 8, fire_nedenleri: (f0.fire_nedenleri || []).map((x) => x.trim()).filter(Boolean) };
    if (f.id) await api.put(`/istasyonlar/${f.id}`, f); else await api.post("/istasyonlar", f);
    await api.post("/maliyet/yeniden-hesapla");
    toast.success("Kaydedildi"); load();
  };
  const sil = async (id) => {
    try { await api.delete(`/istasyonlar/${id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const tasi = async (i, yon) => {
    const a = list[i], b = list[i + yon];
    if (!b) return;
    await Promise.all([api.put(`/istasyonlar/${a.id}`, { ...a, sira: b.sira }), api.put(`/istasyonlar/${b.id}`, { ...b, sira: a.sira })]);
    load();
  };

  const aktifler = list.filter((s) => s.aktif);

  return (
    <div data-testid="istasyonlar-page">
      <PageHeader eyebrow="Üretim yapılandırması" title="İstasyon Akışı" desc="İş emirleri bu zincire göre istasyondan istasyona, personelden personele devredilir.">
        {isYonetim && <GradButton data-testid="istasyon-ekle" onClick={() => setDlg({ ad: "", kod: "", tip: "uretim", sira: list.length + 1, kapasite_gunluk: 0, saatlik_maliyet: 0, maliyet_para: "USD", lokasyon_id: null, aktif: true, fire_nedenleri: [], gunluk_calisma_saat: 8 })}><Plus className="w-4 h-4" />Yeni istasyon</GradButton>}
      </PageHeader>
      <GlassCard className="p-6 mb-6 overflow-x-auto erp-scroll fade-up" data-testid="istasyon-zincir">
        <div className="flex items-center gap-2 min-w-max">
          {aktifler.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2">
              <div className="rounded-2xl px-5 py-4 bg-white/[0.04] grad-border min-w-[150px]">
                <div className="text-[10px] font-mono text-muted-foreground">{String(i + 1).padStart(2, "0")} · {TIP_ETIKET[s.tip]}</div>
                <div className="font-semibold text-sm mt-1">{s.ad}</div>
              </div>
              {i < aktifler.length - 1 && <ChevronRight className="w-5 h-5 text-[var(--g2)]" />}
            </div>
          ))}
        </div>
      </GlassCard>
      <GlassCard className="overflow-x-auto">
        {list.length === 0 ? <EmptyState text="İstasyon yok." testid="istasyon-bos" /> : (
          <table className="w-full text-sm" data-testid="istasyon-tablo">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-white/5">
              <th className="p-4">Sıra</th><th className="p-4">İstasyon</th><th className="p-4">Tip</th><th className="p-4">Lokasyon</th><th className="p-4 text-right">Kapasite/gün</th><th className="p-4 text-right">Maliyet (saat)</th><th className="p-4" />
            </tr></thead>
            <tbody>
              {list.map((s, i) => (
                <tr key={s.id} className={`border-b border-white/5 ${s.aktif ? "" : "opacity-50"}`} data-testid={`istasyon-satir-${s.kod}`}>
                  <td className="p-4 font-mono">{s.sira}</td>
                  <td className="p-4"><div className="font-medium">{s.ad}</div><div className="text-xs font-mono text-muted-foreground">{s.kod}</div>{s.fire_nedenleri?.length > 0 && <div className="text-[11px] text-amber-300/80 mt-0.5" data-testid={`istasyon-fire-${s.kod}`}>Fire: {s.fire_nedenleri.join(" · ")}</div>}</td>
                  <td className="p-4">{TIP_ETIKET[s.tip]}</td>
                  <td className="p-4 text-muted-foreground">{loks.find((l) => l.id === s.lokasyon_id)?.ad || "—"}</td>
                  <td className="p-4 text-right font-mono">{s.kapasite_gunluk || "—"}</td>
                  <td className="p-4 text-right font-mono" data-testid={`istasyon-maliyet-${s.kod}`}>{(s.saatlik_maliyet || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {s.maliyet_para || "USD"}</td>
                  <td className="p-4 text-right whitespace-nowrap">
                    {isYonetim && <>
                      <button data-testid={`istasyon-yukari-${s.kod}`} onClick={() => tasi(i, -1)} disabled={i === 0} className="p-2 rounded-lg text-muted-foreground hover:text-white disabled:opacity-20"><ArrowUp className="w-4 h-4" /></button>
                      <button data-testid={`istasyon-asagi-${s.kod}`} onClick={() => tasi(i, 1)} disabled={i === list.length - 1} className="p-2 rounded-lg text-muted-foreground hover:text-white disabled:opacity-20"><ArrowDown className="w-4 h-4" /></button>
                      <button data-testid={`istasyon-duzenle-${s.kod}`} onClick={() => setDlg(s)} className="p-2 rounded-lg text-muted-foreground hover:text-white"><Pencil className="w-4 h-4" /></button>
                      <ConfirmDelete testid={`istasyon-sil-${s.kod}`} onConfirm={() => sil(s.id)} />
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </GlassCard>
      {dlg && <FormDialog prefix="istasyon" title={dlg.id ? "İstasyonu düzenle" : "Yeni istasyon"} fields={fields} initial={dlg} onSubmit={kaydet} onClose={() => setDlg(null)} />}
    </div>
  );
}
